import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, ShieldCheck, Truck, Heart, Star, Gift, Instagram, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Footer } from '@/components/Footer';
import { TwitterCardsRow } from '@/components/TwitterCardsRow';
import { Reveal } from '@/components/Reveal';
import { useInView } from '@/hooks/useInView';
import { useApp } from '@/state/AppContext';
import { inr, classNames } from '@/lib/format';
import { imgSrc } from '@/lib/img';

// Brand product photography for the "twitter cards" composition
const PHOTO_CHOCOPOPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/1ob20q2i_Gemini_Generated_Image_fg15j0fg15j0fg15.png';
const PHOTO_CHOCODIGITS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/qi8ii2iu_Copy%20of%20WhatsApp%20Image%202026-04-01%20at%205.59.07%20PM%20%281%29.jpeg';
const PHOTO_CHOCOSNAPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/yj5r6qfm_Gemini_Generated_Image_vuus3vuus3vuus3v.png';
// Brand logos shown as the circular badge on each card
const LOGO_CHOCOPOPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/2k1jrqk8_3D%20Chocopops%20Logo.svg';
const LOGO_CHOCODIGITS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/dp7s81oo_3D%20Choco%20Digits.svg';
const LOGO_CHOCOSNAPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/3aoengmm_ChocoSnaps%20Logo.svg';

// Brand-supplied decorative backgrounds (cyan + palm fronds + accents) for the Jungle banner.
// Each SVG is art-directed for its breakpoint width:
//   xl  (>=1280px) → 1099w   ·   lg (>=1024px) → 925w
//   md  (>=768px)  → 664w    ·   sm (default)  → 402w mobile
const JUNGLE_BG_XL = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/77eem76w_Gather%20your%20own%20Jungle%202.svg';
const JUNGLE_BG_LG = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/bm95m89s_Gather%20your%20own%20Jungle%20Refrence%205.svg';
const JUNGLE_BG_MD = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/jzhntxsy_Gather%20your%20own%20Jungle%20Refrence%206.svg';
const JUNGLE_BG_SM = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/4s2m84hb_Gather%20your%20own%20Jungle%20Refrence%207.svg';

// Brand product photography (mock fallbacks + real where supplied)
const IMG_CHOCOPOP = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/gjtf11kb_Chocopop%208.2.jpg';
const IMG_CHOCOSNAP = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/ptp39kef_Chocosnap%202.jpg';
const IMG_CHOCODIGIT = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/nz4ke9od_Choco%20Digit%209.jpeg';
const IMG_HERO = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/j654c959_Landing%20Image%20-%202.png';
// Pink Customiser banner — brand-supplied SVG backgrounds, art-directed per breakpoint
const CUST_BG_XL = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/oljeyqme_Customiser%203.svg';
const CUST_BG_LG = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/4eu9ieq0_Customiser%20Refrence%209.svg';
const CUST_BG_MD = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/whb1xbww_Customiser%20Refrence%2010.svg';
const CUST_BG_SM = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/68uo837o_Customiser%20Refrence%208.svg';

// Jungle section assets
const JUNGLE_FG_DESKTOP = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/u0avi83k_Gather%20your%20own%20jungle%20Full%20Size%20Illustration.png';
const JUNGLE_FG_MOBILE = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/y1ywd724_Gather%20your%20own%20jungle%20%20Mobile%20Illustration.png';

// Customiser carousel — brand-supplied ChocoSnaps lifestyle shots (Birthday, Cat, Anime, Holi, Anniversary)
const CUSTOM_GALLERY = [
  'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/jr39zkot_Gemini_Generated_Image_36457w36457w3645.png',
  'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/r6wcmh19_Gemini_Generated_Image_epixwcepixwcepix.png',
  'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/iz8oa66n_Gemini_Generated_Image_f3dc9ff3dc9ff3dc.png',
  'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/pbs5vo3b_Gemini_Generated_Image_hlayaohlayaohlay.png',
  'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/7ds2ym7t_Gemini_Generated_Image_uc9yr6uc9yr6uc9y.png',
];

// Reusable solid vector decorations
const Squiggle = ({ className = '', color = '#ffbf00' }) => (
  <svg viewBox="0 0 100 20" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M2 10 Q15 0 28 10 T54 10 T80 10 T98 10" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const StarBurst = ({ className = '', color = '#ffbf00' }) => (
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M50 5 L57 38 L92 38 L63 60 L72 92 L50 73 L28 92 L37 60 L8 38 L43 38 Z" fill={color} stroke="#1b1b3a" strokeWidth="3" strokeLinejoin="round" />
  </svg>
);

// Jungle silhouettes (solid vectors, no shadows)
const PalmTree = ({ className = '', color = '#1f6500' }) => (
  <svg viewBox="0 0 100 140" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M48 130 L52 130 L54 60 L46 60 Z" fill="#5a3a1a" />
    <path d="M50 60 Q20 35 5 50 Q25 40 50 60 Z" fill={color} />
    <path d="M50 60 Q80 35 95 50 Q75 40 50 60 Z" fill={color} />
    <path d="M50 60 Q30 25 20 5 Q40 25 50 60 Z" fill={color} />
    <path d="M50 60 Q70 25 80 5 Q60 25 50 60 Z" fill={color} />
    <path d="M50 60 Q50 35 50 10 Q50 35 50 60 Z" fill={color} />
  </svg>
);

const Leaf = ({ className = '', color = '#298700' }) => (
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M50 5 C20 20 10 60 40 90 C70 80 90 40 50 5 Z" fill={color} />
    <path d="M50 12 L50 85" stroke="#fff" strokeWidth="2" fill="none" />
  </svg>
);

const Monkey = ({ className = '' }) => (
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <circle cx="50" cy="55" r="32" fill="#7a4a2a" />
    <circle cx="36" cy="42" r="10" fill="#7a4a2a" />
    <circle cx="64" cy="42" r="10" fill="#7a4a2a" />
    <circle cx="36" cy="42" r="5" fill="#f4d4a0" />
    <circle cx="64" cy="42" r="5" fill="#f4d4a0" />
    <ellipse cx="50" cy="62" rx="18" ry="14" fill="#f4d4a0" />
    <circle cx="42" cy="56" r="2.5" fill="#1b1b3a" />
    <circle cx="58" cy="56" r="2.5" fill="#1b1b3a" />
    <path d="M44 68 Q50 73 56 68" stroke="#1b1b3a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
  </svg>
);

// Testimonials data + staggered grid (each card rotate-ins when grid enters viewport)
const TESTIMONIALS = [
  { name: 'Aanya M.', role: 'Parent, Mumbai', text: 'My son\u2019s 5th birthday was a hit \u2014 the lion pops were stolen by every adult too. Repeat order incoming.', accent: 'var(--bb-cyan)' },
  { name: 'Karan T.', role: 'HR, Pune Startup', text: 'We ordered 200 Choco Snaps with our logo for a launch event. The preview tool saved us so much back-and-forth.', accent: 'var(--bb-pink)' },
  { name: 'Riya S.', role: 'Mom of twins', text: 'Choco Digit 4 and 4 again \u2014 my twins are obsessed. Packaging is gift-ready, no extra wrapping needed.', accent: 'var(--bb-yellow)' },
];

const TestimonialsGrid = () => {
  const { ref, inView } = useInView();
  return (
    <div ref={ref} className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {TESTIMONIALS.map((t, i) => (
        <div
          key={t.name}
          className={`rounded-2xl bg-white p-5 bb-press ${inView ? 'bb-anim-rotate-in' : 'opacity-0'}`}
          style={{ border: `3px solid ${t.accent}`, animationDelay: inView ? `${i * 120}ms` : undefined }}
          data-testid={`home-testimonial-${i}`}
        >
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} className="w-4 h-4 fill-[var(--bb-yellow)] text-[var(--bb-yellow)]" />
            ))}
          </div>
          <p className="mt-3 text-sm font-nunito-medium text-[var(--bb-darkgrey)]">{t.text}</p>
          <div className="mt-3 text-xs font-nunito-black text-[var(--bb-darkblue)]">
            {t.name} · <span className="font-nunito-medium text-[var(--bb-darkgrey)]">{t.role}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const HomePage = () => {
  const { config } = useApp();
  const [galleryIdx, setGalleryIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setGalleryIdx((i) => (i + 1) % CUSTOM_GALLERY.length), 3500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="bg-white">
      {/* Promo bar */}
      <div className="bg-[var(--bb-cyan)] text-white text-center text-xs md:text-sm font-nunito-black py-2 px-3 uppercase tracking-wider">
        Free shipping on orders over {inr(config.free_shipping_threshold)} · Use code <span className="underline">BASH10</span> for 10% off
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-8 md:pt-14 pb-6 md:pb-12">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center">
            {/* LEFT - copy */}
            <div className="relative">
              <Squiggle className="absolute -top-4 left-0 w-28 hidden md:block" color="#01a0b9" />
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs md:text-sm font-nunito-black uppercase tracking-wider mb-4">
                <Sparkles className="w-4 h-4" /> Joyful, Customisable, Precise
              </div>
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.95] text-[var(--bb-darkblue)]">
                Chocolates that <span className="text-[var(--bb-pink)]">POP</span>,<br />
                <span className="text-[var(--bb-cyan)]">SNAP</span> &amp; <span className="text-[var(--bb-yellow)]">COUNT</span>.
              </h1>

              <p className="mt-4 text-base md:text-lg font-nunito-medium text-[var(--bb-darkgrey)] max-w-xl">
                Built for kids who love adventure, parents who love a fuss-free treat, and brands who want gifts that get remembered.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="h-12 px-6 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black bb-press-pop bb-shimmer-btn group" data-testid="home-hero-shop-button">
                  <Link to="/shop">Browse the shop <ArrowRight className="w-4 h-4 ml-1 bb-hover-arrow-target" /></Link>
                </Button>
                <Button asChild className="h-12 px-6 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white font-nunito-black bb-press-pop bb-shimmer-btn group" data-testid="home-hero-customize-button">
                  <Link to="/customize"><Sparkles className="w-4 h-4 mr-1 bb-hover-wobble-target" /> Customise yours</Link>
                </Button>
              </div>

              <div className="mt-4 inline-flex items-center gap-2 text-xs md:text-sm text-[var(--bb-darkgrey)] font-nunito-bold">
                <Truck className="w-4 h-4 text-[var(--bb-cyan)]" /> Fast delivery · Ships to select locations
              </div>
            </div>

            {/* RIGHT - hero composition image with floating tags */}
            <div className="relative h-[360px] sm:h-[440px] md:h-[480px] lg:h-[520px] flex items-center justify-center">
              <img
                src={imgSrc(IMG_HERO, 'hero')}
                alt="Big Bash chocolate hero — cow Chocopop, digit and Happy Birthday Chocosnap"
                className="relative z-10 w-full h-full object-contain bb-anim-float"
                decoding="async"
                data-testid="home-hero-image"
              />

              {/* Floating chips - positioned around the image */}
              <span className="absolute top-4 left-2 md:top-6 md:left-4 z-20 px-3 py-1 rounded-full bg-[var(--bb-pink)] text-white text-xs font-nunito-black uppercase tracking-wider bb-anim-wiggle" style={{ animationDelay: '0s' }}>Yum!</span>
              <span className="absolute top-1/2 -translate-y-1/2 right-2 md:right-6 z-20 px-3 py-1 rounded-full bg-[var(--bb-cyan)] text-white text-xs font-nunito-black uppercase tracking-wider bb-anim-wiggle" style={{ animationDelay: '0.3s' }}>Fun!</span>
              <span className="absolute bottom-6 left-6 md:bottom-10 md:left-8 z-20 px-3 py-1 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs font-nunito-black uppercase tracking-wider bb-anim-wiggle" style={{ animationDelay: '0.6s' }}>Wow!</span>
              <span className="absolute bottom-2 right-4 md:bottom-6 md:right-12 z-20 px-3 py-1 rounded-full bg-[var(--bb-darkblue)] text-white text-[10px] md:text-xs font-nunito-black uppercase tracking-wider">Made to order, just for you</span>

              {/* Decorative stickers */}
              <StarBurst className="absolute top-2 left-1/3 w-10 h-10 z-20 bb-anim-wiggle" color="#ffbf00" />
              <div className="absolute top-2 right-2 md:top-4 md:right-4 z-20 bg-white text-[var(--bb-pink)] rounded-full px-3 py-1 text-[11px] font-nunito-black uppercase tracking-wider border-2 border-[var(--bb-pink)]">New Drop</div>
            </div>
          </div>
        </div>

        {/* EXPLORE OUR PRODUCTS — twitter cards */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-12 md:pb-16">
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs font-nunito-black uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Our line-up
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--bb-darkblue)] mt-3"><span className="bb-underline">Explore our Products</span></h2>
            <p className="text-sm md:text-base font-nunito-medium text-[var(--bb-darkgrey)] mt-1 max-w-xl mx-auto md:mx-0">Three signature products. Hand-finished, made-to-order, ready to wow.</p>
          </div>

          <TwitterCardsRow
            className="mt-6"
            ctaLabel="Go to Shop"
            cards={[
              {
                to: '/product/chocopops-pack-of-24',
                tone: 'cyan',
                photo: PHOTO_CHOCOPOPS,
                logo: LOGO_CHOCOPOPS,
                packSize: 24,
                title: '3D Choco Pops',
                body: '8 Animal Chocopops. Pack of 24.',
                price: '₹1,189',
                testid: 'home-explore-card-chocopops',
              },
              {
                to: '/product/chocosnaps-pack-of-48',
                tone: 'yellow',
                photo: PHOTO_CHOCOSNAPS,
                logo: LOGO_CHOCOSNAPS,
                packSize: 48,
                title: 'Choco Snaps',
                body: 'Customisable design. Pack of 48.',
                price: '₹1,189',
                testid: 'home-explore-card-chocosnaps',
              },
              {
                to: '/product/choco-digits-pack-of-20',
                tone: 'pink',
                photo: PHOTO_CHOCODIGITS,
                logo: LOGO_CHOCODIGITS,
                packSize: 20,
                title: '3D Choco Digits',
                body: '0–9 on a stick. Pack of 20.',
                price: '₹1,189',
                comingSoon: true,
                ctaLabelOverride: 'Notify me',
                testid: 'home-explore-card-chocodigits',
              },
            ]}
          />
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { Icon: ShieldCheck, label: 'FSSAI Certified', sub: 'Food-safe + edible inks' },
            { Icon: Truck, label: 'Fast Delivery', sub: 'Ships to select locations' },
            { Icon: Heart, label: 'Loved by 10,000+', sub: 'Parents & corporates' },
            { Icon: Gift, label: 'Made to gift', sub: 'Beautiful packaging' },
          ].map(({ Icon, label, sub }) => (
            <div key={label} className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/10 p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--bb-yellow-soft)] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[var(--bb-cyan)]" />
              </div>
              <div>
                <div className="text-sm font-nunito-black leading-tight text-[var(--bb-darkblue)]">{label}</div>
                <div className="text-[11px] text-[var(--bb-darkgrey)] font-nunito-medium">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Customizer explainer - with carousel */}
      {/* ── Customiser banner per brand spec (pink, brand SVG bg, carousel) ── */}
      <Reveal as="section" className="max-w-6xl mx-auto px-4 md:px-6 mt-12">
        <div
          className="relative rounded-3xl overflow-hidden bg-[#E50D7A] min-h-0 sm:min-h-[460px] md:min-h-[520px] lg:min-h-[560px]"
          data-testid="home-customiser-banner"
        >
          {/* Brand-supplied decorative pink background with sparkle stars */}
          <picture aria-hidden="true">
            <source media="(min-width: 1280px)" srcSet={CUST_BG_XL} />
            <source media="(min-width: 1024px)" srcSet={CUST_BG_LG} />
            <source media="(min-width: 768px)" srcSet={CUST_BG_MD} />
            <img
              src={CUST_BG_SM}
              alt=""
              className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
              loading="lazy"
            />
          </picture>

          {/* ── MOBILE LAYOUT (default → sm): carousel on top, text below ── */}
          <div className="sm:hidden relative z-10 flex flex-col">
            <div className="px-5 pt-5 pb-2">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-[#7ad3e0] shadow-[0_8px_24px_rgba(0,0,0,0.22)]">
                {CUSTOM_GALLERY.map((src, i) => (
                  <img
                    key={src}
                    src={imgSrc(src, 'card')}
                    alt={`Customisation example ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === galleryIdx ? 'bb-anim-ken-burns' : ''}`}
                    style={{ opacity: i === galleryIdx ? 1 : 0 }}
                    loading="lazy"
                    decoding="async"
                  />
                ))}
                <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                  {CUSTOM_GALLERY.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setGalleryIdx(i)}
                      aria-label={`Show example ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${i === galleryIdx ? 'bg-white w-5' : 'bg-white/60 w-1.5'}`}
                    />
                  ))}
                </div>
                <img
                  src="https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/3aoengmm_ChocoSnaps%20Logo.svg"
                  alt=""
                  className="absolute -bottom-5 -right-3 w-24 h-24 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.22)] select-none pointer-events-none z-10 bb-anim-float"
                />
              </div>
            </div>
            <div className="px-5 pt-6 pb-6 flex flex-col">
              <span className="inline-flex self-start items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-[var(--bb-pink)] text-xs font-nunito-black">
                Customiser
              </span>
              <h2 className="font-display text-[42px] mt-4 text-white leading-[0.95]">
                Make them yours in two sweet steps!
              </h2>
              <ol className="mt-6 relative">
                <span aria-hidden="true" className="absolute left-[11px] top-6 bottom-6 w-px bg-[var(--bb-yellow)]/70" />
                {['Pick a preset or upload your own image', 'Preview and Add to Cart!'].map((label, idx) => (
                  <li key={label} className="relative flex items-start gap-3 mb-3 last:mb-0">
                    <span className="relative z-10 w-6 h-6 rounded-full bg-[var(--bb-pink)] border-2 border-[var(--bb-yellow)] text-white font-nunito-black text-[11px] flex items-center justify-center shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-white font-nunito-medium pt-0.5">{label}</span>
                  </li>
                ))}
              </ol>
              <Link
                to="/customize"
                className="mt-6 inline-flex items-center justify-center w-full h-14 rounded-2xl bg-[var(--bb-yellow)] hover:bg-[#e6ac00] text-[var(--bb-darkblue)] font-nunito-black text-lg transition-colors bb-press-pop bb-shimmer-btn group"
                data-testid="home-customiser-cta-mobile"
              >
                Start Customising!
              </Link>
            </div>
          </div>

          {/* ── DESKTOP LAYOUT (sm+): text on left, padded portrait carousel on right ── */}
          <div className="hidden sm:block">
            <div className="absolute right-[5%] md:right-[6%] lg:right-[7%] top-[10%] bottom-[14%] aspect-[3/4] max-w-[420px]">
              <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#7ad3e0] shadow-[0_10px_36px_rgba(0,0,0,0.22)]">
                {CUSTOM_GALLERY.map((src, i) => (
                  <img
                    key={src}
                    src={imgSrc(src, 'main')}
                    alt={`Customisation example ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === galleryIdx ? 'bb-anim-ken-burns' : ''}`}
                    style={{ opacity: i === galleryIdx ? 1 : 0 }}
                    loading="lazy"
                    decoding="async"
                    data-testid={i === 0 ? 'home-customiser-carousel' : undefined}
                  />
                ))}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                  {CUSTOM_GALLERY.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setGalleryIdx(i)}
                      aria-label={`Show example ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${i === galleryIdx ? 'bg-white w-5' : 'bg-white/60 w-1.5'}`}
                    />
                  ))}
                </div>
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/3aoengmm_ChocoSnaps%20Logo.svg"
                alt=""
                className="absolute -bottom-5 -right-5 md:-bottom-6 md:-right-6 w-20 h-20 md:w-28 md:h-28 lg:w-32 lg:h-32 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.22)] select-none pointer-events-none bb-anim-float"
              />
            </div>

            <div className="relative z-10 p-6 sm:p-8 md:p-10 lg:p-12 w-[54%] md:w-[55%] lg:w-[55%] flex flex-col justify-center sm:min-h-[460px] md:min-h-[520px] lg:min-h-[560px]">
              <span className="inline-flex self-start items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-[var(--bb-pink)] text-xs md:text-sm font-nunito-black">
                Customiser
              </span>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-[56px] mt-4 text-white leading-[0.95]">
                Make them yours in two sweet steps!
              </h2>
              <ol className="mt-6 md:mt-8 relative">
                <span aria-hidden="true" className="absolute left-[11px] top-6 bottom-6 w-px bg-[var(--bb-yellow)]/70" />
                {['Pick a preset or upload your own image', 'Preview and Add to Cart!'].map((label, idx) => (
                  <li key={label} className="relative flex items-start gap-3 mb-3 last:mb-0">
                    <span className="relative z-10 w-6 h-6 rounded-full bg-[var(--bb-pink)] border-2 border-[var(--bb-yellow)] text-white font-nunito-black text-[11px] flex items-center justify-center shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-sm md:text-base text-white font-nunito-medium pt-0.5">{label}</span>
                  </li>
                ))}
              </ol>
              <Link
                to="/customize"
                className="mt-6 md:mt-8 inline-flex items-center justify-center h-12 md:h-14 px-6 rounded-2xl bg-[var(--bb-yellow)] hover:bg-[#e6ac00] text-[var(--bb-darkblue)] font-nunito-black text-base md:text-lg self-start transition-colors w-full sm:w-auto sm:min-w-[260px] bb-press-pop bb-shimmer-btn group"
                data-testid="home-customiser-cta"
              >
                Start Customising!
              </Link>
            </div>
          </div>
        </div>
      </Reveal>

      {/* GATHER YOUR OWN JUNGLE - banner per brand spec.
          Mobile: vertical stack (text on top, animals + yellow circle below).
          Desktop: horizontal split (animals + circle left, text right). */}
      <Reveal as="section" className="max-w-6xl mx-auto px-4 md:px-6 mt-12">
        <div
          className="relative rounded-3xl overflow-hidden bg-[#11B6CA] min-h-[600px] sm:min-h-[640px] md:min-h-[540px] lg:min-h-[600px]"
          data-testid="home-jungle-section"
        >
          {/* ── Brand-supplied decorative background (cyan + palm fronds + accents) ── */}
          <picture aria-hidden="true">
            <source media="(min-width: 1280px)" srcSet={JUNGLE_BG_XL} />
            <source media="(min-width: 1024px)" srcSet={JUNGLE_BG_LG} />
            <source media="(min-width: 768px)" srcSet={JUNGLE_BG_MD} />
            <img
              src={JUNGLE_BG_SM}
              alt=""
              className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
              loading="lazy"
            />
          </picture>

          {/* ── MOBILE LAYOUT (default → md): vertical stack ── */}
          <div className="md:hidden relative z-10 flex flex-col h-full min-h-[680px]">
            {/* Top: text block, padded */}
            <div className="px-6 pt-7 pb-4">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-[var(--bb-darkblue)] text-xs font-nunito-black">
                Build your Pack
              </span>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 text-white leading-[0.95]">
                Gather your own Jungle!
              </h2>
              <p className="mt-4 text-sm text-white font-nunito-medium leading-relaxed">
                Pack of 24 Chocopops. Pick your favourite characters from Simba the Lion to Gigi the giraffe.
              </p>
              <p className="mt-2 text-sm text-white font-nunito-medium">
                8 different animals available for your choosing.
              </p>
              <Link
                to="/product/chocopops-pack-of-24/customize"
                className="mt-5 inline-flex items-center justify-center w-full h-12 rounded-2xl bg-[var(--bb-yellow)] hover:bg-[#e6ac00] text-[var(--bb-darkblue)] font-nunito-black text-base transition-colors bb-press-pop bb-shimmer-btn group"
                data-testid="home-jungle-cta-mobile"
              >
                Gather&rsquo;em all!
              </Link>
            </div>
            {/* Bottom: animal composition with yellow sun-circle behind, centered */}
            <div className="relative flex-1 min-h-[340px] mt-2">
              {/* Yellow sun-circle: horizontally centered, sits behind illustration */}
              <div
                className="absolute rounded-full bg-[var(--bb-yellow)] left-1/2 -translate-x-1/2"
                style={{
                  width: '82%',
                  aspectRatio: '1 / 1',
                  bottom: '-6%',
                }}
                aria-hidden="true"
              />
              {/* Animal illustration — anchored to sun-circle */}
              <img
                src={imgSrc(JUNGLE_FG_MOBILE, 'main')}
                alt="Big Bash jungle chocopops — lion, unicorn, panda, cow and giraffe lollipops"
                className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[104%] w-auto max-w-[100%] object-contain object-bottom select-none pointer-events-none"
                loading="lazy"
                decoding="async"
                data-testid="home-jungle-illustration-mobile"
              />
              {/* 3D ChocoPops circular brand logo, bottom-left overlay (matches desktop) */}
              <img
                src={LOGO_CHOCOPOPS}
                alt=""
                className="absolute bottom-4 left-4 w-20 h-20 sm:w-24 sm:h-24 z-10 drop-shadow-[0_4px_10px_rgba(0,0,0,0.22)] select-none pointer-events-none"
                data-testid="home-jungle-logo-mobile"
              />
            </div>
          </div>

          {/* ── DESKTOP LAYOUT (md+): horizontal split ── */}
          <div className="hidden md:block">
            {/* Big yellow sun-circle behind the animal composition */}
            <div
              className="absolute rounded-full bg-[var(--bb-yellow)]"
              style={{
                width: 'min(48%, 460px)',
                aspectRatio: '1 / 1',
                left: '4%',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              aria-hidden="true"
            />
            {/* Animal illustration */}
            <img
              src={imgSrc(JUNGLE_FG_DESKTOP, 'hero')}
              alt="Big Bash jungle chocopops — lion, unicorn, panda, cow and giraffe lollipops"
              className="absolute bottom-0 left-[4%] h-[92%] lg:h-[98%] w-auto max-w-[50%] object-contain object-bottom select-none pointer-events-none"
              loading="lazy"
              decoding="async"
              data-testid="home-jungle-illustration"
            />
            {/* 3D Choco Pops circular logo, bottom-left */}
            <img
              src={LOGO_CHOCOPOPS}
              alt=""
              className="absolute bottom-6 left-6 w-28 h-28 lg:w-32 lg:h-32 z-10 drop-shadow-[0_4px_10px_rgba(0,0,0,0.22)] select-none pointer-events-none"
              data-testid="home-jungle-logo"
            />
            {/* Right side: content block */}
            <div className="relative z-10 ml-auto h-full flex items-center" style={{ width: 'min(48%, 460px)' }}>
              <div className="p-10 lg:p-12 w-full">
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-[var(--bb-darkblue)] text-sm font-nunito-black">
                  Build your Pack
                </span>
                <h2 className="font-display text-5xl lg:text-[64px] mt-4 text-white leading-[0.95]">
                  Gather your own Jungle!
                </h2>
                <p className="mt-5 text-base text-white font-nunito-medium leading-relaxed">
                  Pack of 24 Chocopops. Pick your favourite characters from Simba the Lion to Gigi the giraffe.
                </p>
                <p className="mt-3 text-base text-white font-nunito-medium">
                  8 different animals available for your choosing.
                </p>
                <Link
                  to="/product/chocopops-pack-of-24/customize"
                  className="mt-6 inline-flex items-center justify-center w-full h-14 rounded-2xl bg-[var(--bb-yellow)] hover:bg-[#e6ac00] text-[var(--bb-darkblue)] font-nunito-black text-lg transition-colors bb-press-pop bb-shimmer-btn group"
                  data-testid="home-jungle-cta"
                >
                  Gather&rsquo;em all!
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Bulk Orders inline section */}
      <Reveal as="section" className="max-w-6xl mx-auto px-4 md:px-6 mt-12">
        <div className="rounded-3xl overflow-hidden bb-block-dark p-6 md:p-10 grid md:grid-cols-3 gap-4 items-center">
          <div className="md:col-span-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs font-nunito-black uppercase tracking-wider">
              <Gift className="w-3.5 h-3.5" /> Gifting Favourites
            </div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 text-white">Bulk Orders that Wow</h2>
            <p className="text-sm md:text-base text-white/85 mt-2 max-w-xl font-nunito-medium">Custom branding on every piece, festive packaging, dedicated account support. Minimum 50 pieces.</p>
          </div>
          <div className="flex flex-col gap-2">
            <Button asChild className="h-12 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black">
              <Link to="/bulk-orders">Request a Quote</Link>
            </Button>
            <Button asChild variant="outline" className="h-12 rounded-xl border-2 border-[var(--bb-yellow)] text-[var(--bb-yellow)] bg-transparent hover:bg-[var(--bb-yellow)] hover:text-[var(--bb-darkblue)] font-nunito-black">
              <a href={`https://wa.me/${config.whatsapp_number}?text=Hi%20Big%20Bash%2C%20I%27m%20interested%20in%20bulk%20orders.`} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp Us</a>
            </Button>
          </div>
        </div>
      </Reveal>

      {/* Testimonials - WHITE FILL with rotating borders */}
      <Reveal as="section" className="max-w-6xl mx-auto px-4 md:px-6 mt-12">
        <h2 className="font-display text-3xl md:text-4xl text-[var(--bb-darkblue)]">
          <span className="bb-underline">Loved by little ones (and big ones)</span>
        </h2>
        <TestimonialsGrid />
      </Reveal>

      {/* Instagram - simple follow card */}
      <Reveal as="section" className="max-w-6xl mx-auto px-4 md:px-6 mt-12">
        <a
          href={`https://www.instagram.com/${config.instagram_handle || 'bigbashconfectioneries'}`}
          target="_blank"
          rel="noreferrer"
          className="block rounded-3xl bb-block-pink p-6 md:p-8 bb-press"
          data-testid="home-ig-follow"
        >
          <div className="flex flex-col sm:flex-row items-center gap-4 text-white">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center">
              <Instagram className="w-7 h-7 text-[var(--bb-pink)]" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-display text-2xl md:text-3xl">Follow our daily chocolate adventures</div>
              <div className="text-sm font-nunito-medium text-white/90">@{config.instagram_handle || 'bigbashconfectioneries'} on Instagram</div>
            </div>
            <Badge className="bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] rounded-full px-4 py-1.5 font-nunito-black hover:bg-[#e6ac00]">Tap to follow</Badge>
          </div>
        </a>
      </Reveal>

      <Footer />
    </div>
  );
};

export default HomePage;
